import { Mic } from "lucide-react";

import {
  useAudioInputDeviceSelect,
  useMediaDevices,
  useMicrophoneDeviceId,
} from "@elation/transcribe";

import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/shared/components/ui/select";

export interface VisitMicrophoneSelectViewProps {
  devices: MediaDeviceInfo[];
  selectedDeviceId: string | null;
  onSelect: (deviceId: string | null) => void;
  variant?: "inline" | "field";
}

export function VisitMicrophoneSelectView({
  devices,
  selectedDeviceId,
  onSelect,
  variant = "inline",
}: VisitMicrophoneSelectViewProps) {
  const hasDevices = devices.length > 0;

  const select = (
    <Select
      disabled={!hasDevices}
      value={selectedDeviceId ?? ""}
      onValueChange={(value) => onSelect(value || null)}
    >
      <SelectTrigger
        id="visit-microphone-select"
        aria-label="Microphone"
        className={
          variant === "field"
            ? "h-9 w-full"
            : "h-6 min-w-0 border-0 bg-transparent px-0 text-[12px] font-medium text-foreground shadow-none focus:ring-0"
        }
      >
        <SelectValue
          placeholder={
            hasDevices ? "Choose a microphone" : "No microphones found"
          }
        />
      </SelectTrigger>
      <SelectContent>
        {devices.map((device) => (
          <SelectItem key={device.deviceId} value={device.deviceId}>
            {device.label || "Microphone"}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (variant === "field") {
    return select;
  }

  return (
    <HorizontalFlexbox
      alignItems="center"
      className="h-8 w-[220px] gap-2 rounded-[9px] border border-primary/25 bg-primary/5 px-2"
    >
      <span
        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
        aria-hidden="true"
      >
        <Mic className="size-3.5" />
      </span>
      <VerticalFlexbox className="min-w-0 flex-1">{select}</VerticalFlexbox>
    </HorizontalFlexbox>
  );
}

export interface VisitMicrophoneSelectProps {
  variant?: "inline" | "field";
}

export function VisitMicrophoneSelect({
  variant = "inline",
}: VisitMicrophoneSelectProps) {
  const {
    devices,
    selectedDeviceId,
    selectDevice: onSelect,
  } = useAudioInputDeviceSelect();

  return (
    <VisitMicrophoneSelectView
      devices={devices}
      selectedDeviceId={selectedDeviceId}
      onSelect={onSelect}
      variant={variant}
    />
  );
}

export function VisitMicrophoneField({
  variant = "field",
}: Pick<VisitMicrophoneSelectProps, "variant">) {
  const [mediaDevices] = useMediaDevices();
  const [selectedDeviceId, setMicrophoneDeviceId] = useMicrophoneDeviceId();

  return (
    <VisitMicrophoneSelectView
      devices={mediaDevices ?? []}
      selectedDeviceId={selectedDeviceId}
      onSelect={setMicrophoneDeviceId}
      variant={variant}
    />
  );
}
